import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  CloudArrowUp, CheckCircle, WarningCircle, ArrowsClockwise,
  Clock, User, ClockCounterClockwise, GitDiff, Plus, Minus, PencilSimple, ArrowClockwise,
  ShieldCheck, Handshake, XCircle, PaperPlaneTilt, Eye, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePreview } from "@/context/PreviewContext";

const dt = (s) => s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

/* ---------------- History dialog ---------------- */
function HistoryDialog({ open, onOpenChange, kind, kindLabel, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);

  const load = useCallback(async () => {
    if (!kind) return;
    setLoading(true);
    try {
      const r = await api.get(`/platform/publish-history/${kind}`);
      setRows(r.data);
    } catch { toast.error("Failed to load history"); }
    finally { setLoading(false); }
  }, [kind]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const rollback = async (v) => {
    if (!window.confirm(`Rollback ${kindLabel} to v${v.version}? A new version will be created with that content.`)) return;
    setRollingBack(v.version);
    try {
      const r = await api.post(`/platform/publish-rollback/${kind}/${v.version}`);
      toast.success(`✓ Rolled back — new live v${r.data.version}`);
      window.dispatchEvent(new CustomEvent("vyparcrm:config-published", { detail: { kind } }));
      onChanged?.();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rollback failed"); }
    finally { setRollingBack(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="publish-history-dialog">
        <DialogHeader><DialogTitle>Version history · {kindLabel}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Every publish and rollback is an immutable version. Rolling back creates a new version that copies the older content — history stays intact.</p>
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-2 mt-3">
            {rows.map((v) => (
              <div key={v.id} className={"p-3 border rounded-md flex items-center gap-3 " + (v.is_current ? "border-emerald-500/50 bg-emerald-500/5" : "border-border")}
                data-testid={`ph-row-${v.version}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    v{v.version}
                    {v.is_current && <span className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 px-1.5 rounded">LIVE</span>}
                    {v.rolled_back_from && <span className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 border border-amber-500/40 px-1.5 rounded">rollback of v{v.rolled_back_from}</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{dt(v.published_at)}{v.published_by_email && ` · by ${v.published_by_email}`}</div>
                  {v.change_note && <div className="text-xs italic text-muted-foreground mt-1">"{v.change_note}"</div>}
                </div>
                {!v.is_current && (
                  <Button size="sm" onClick={() => rollback(v)} disabled={rollingBack === v.version} data-testid={`ph-rollback-${v.version}`}>
                    <ArrowClockwise size={12} className="mr-1.5" />
                    {rollingBack === v.version ? "Rolling back…" : "Rollback"}
                  </Button>
                )}
              </div>
            ))}
            {!rows.length && <div className="py-6 text-center text-xs text-muted-foreground italic">No published versions yet.</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Diff dialog ---------------- */
function DiffDialog({ open, onOpenChange }) {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/platform/publish-diff")
      .then((r) => setDiff(r.data))
      .catch(() => toast.error("Failed to load diff"))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="publish-diff-dialog">
        <DialogHeader><DialogTitle>Publish Diff Preview</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">What will change on the next publish. Green = added · red = removed · amber = edited (with the changed field names).</p>
        {loading || !diff ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading diff…</div>
        ) : (
          <div className="space-y-4 mt-3">
            {Object.entries(diff).map(([kind, info]) => {
              const total = info.counts.added + info.counts.removed + info.counts.edited;
              if (total === 0) return null;
              return (
                <Card key={kind} className="p-4" data-testid={`diff-${kind}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="font-medium text-sm">{info.label}</div>
                    <div className="text-[11px] text-muted-foreground">+{info.counts.added} · -{info.counts.removed} · ~{info.counts.edited}</div>
                  </div>
                  <div className="space-y-1.5">
                    {info.added.map((r, i) => (
                      <div key={"a" + i} className="text-xs flex items-start gap-2 text-emerald-700 dark:text-emerald-300" data-testid={`diff-${kind}-added-${i}`}>
                        <Plus size={11} weight="bold" className="mt-0.5" />
                        <span>{r.summary}</span>
                      </div>
                    ))}
                    {info.removed.map((r, i) => (
                      <div key={"r" + i} className="text-xs flex items-start gap-2 text-destructive" data-testid={`diff-${kind}-removed-${i}`}>
                        <Minus size={11} weight="bold" className="mt-0.5" />
                        <span>{r.summary}</span>
                      </div>
                    ))}
                    {info.edited.map((e, i) => (
                      <div key={"e" + i} className="text-xs flex items-start gap-2 text-amber-700 dark:text-amber-300" data-testid={`diff-${kind}-edited-${i}`}>
                        <PencilSimple size={11} weight="bold" className="mt-0.5" />
                        <span>{e.summary}<span className="text-muted-foreground"> · {e.changed_fields.join(", ")}</span></span>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
            {Object.values(diff).every((i) => (i.counts.added + i.counts.removed + i.counts.edited) === 0) && (
              <div className="py-8 text-center text-sm text-muted-foreground italic">No pending changes — everything is already live.</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Reject dialog ---------------- */
function RejectDialog({ open, onOpenChange, onSubmit, busy }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reject-dialog">
        <DialogHeader><DialogTitle>Reject publish request</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Tell the requester why so they can fix and re-submit. This is stored on the request record.</p>
        <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Sidebar order looks off — swap Orders and Quotes back."
                  data-testid="reject-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reject-cancel">Cancel</Button>
          <Button onClick={() => onSubmit(reason.trim())} disabled={busy || !reason.trim()} data-testid="reject-submit">
            {busy ? "Rejecting…" : "Reject request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Request row ---------------- */
function RequestRow({ req, currentEmail, onApprove, onReject, onCancel, busyId }) {
  const isRequester = (req.requester_email || "").toLowerCase() === (currentEmail || "").toLowerCase();
  const busy = busyId === req.id;
  const totalCounts = (req.kinds || []).reduce((acc, k) => {
    acc.added += k.counts?.added || 0;
    acc.removed += k.counts?.removed || 0;
    acc.edited += k.counts?.edited || 0;
    return acc;
  }, { added: 0, removed: 0, edited: 0 });
  return (
    <div className="p-4 border border-border rounded-md" data-testid={`pr-row-${req.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium flex items-center gap-2">
            {req.scope === "all" ? "Publish All" : `Publish · ${req.scope}`}
            <span className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 border border-amber-500/40 px-1.5 rounded">pending</span>
            {isRequester && <span className="text-[10px] uppercase tracking-wider text-sky-700 dark:text-sky-300 border border-sky-500/40 px-1.5 rounded">yours</span>}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Requested by <b>{req.requester_email}</b> · {dt(req.requested_at)}
          </div>
          {req.change_note && <div className="text-xs italic text-muted-foreground mt-1">"{req.change_note}"</div>}
          <div className="text-[11px] text-muted-foreground mt-2">
            <span className="text-emerald-700 dark:text-emerald-300">+{totalCounts.added}</span>
            <span className="mx-1.5">·</span>
            <span className="text-destructive">-{totalCounts.removed}</span>
            <span className="mx-1.5">·</span>
            <span className="text-amber-700 dark:text-amber-300">~{totalCounts.edited}</span>
            <span className="ml-2">across {req.kinds?.length || 0} area{req.kinds?.length === 1 ? "" : "s"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isRequester ? (
            <Button size="sm" variant="outline" onClick={() => onCancel(req)} disabled={busy}
                    data-testid={`pr-cancel-${req.id}`}>
              {busy ? "Cancelling…" : "Cancel request"}
            </Button>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => onReject(req)} disabled={busy}
                      data-testid={`pr-reject-${req.id}`}>
                <XCircle size={12} className="mr-1.5" />Reject
              </Button>
              <Button size="sm" onClick={() => onApprove(req)} disabled={busy}
                      data-testid={`pr-approve-${req.id}`}>
                <CheckCircle size={12} className="mr-1.5" />
                {busy ? "Approving…" : "Approve & publish"}
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 mt-3">
        {(req.kinds || []).filter(k => (k.counts?.added || k.counts?.removed || k.counts?.edited)).map((k) => (
          <div key={k.kind} className="text-[11px] flex items-center gap-1.5 px-2 py-1 rounded border border-border bg-secondary/30">
            <span className="font-medium">{k.label}</span>
            <span className="text-muted-foreground">+{k.counts.added} -{k.counts.removed} ~{k.counts.edited}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Main ---------------- */
export default function PublishCenter() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [singleBusy, setSingleBusy] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [tab, setTab] = useState("publish");
  const { user: me } = useAuth();
  const [pendingReqs, setPendingReqs] = useState([]);
  const [reqBusyId, setReqBusyId] = useState(null);
  const [togglingApproval, setTogglingApproval] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [changeNote, setChangeNote] = useState("");
  const { setPreviewMode } = usePreview();
  const [splitPreview, setSplitPreview] = useState(false);
  // Cache-busts the iframe when Publish Center receives a config-published /
  // save-in-a-builder signal so the preview always reflects the latest draft.
  const [iframeKey, setIframeKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/platform/publish-status"),
        api.get("/platform/publish-requests?status=pending"),
      ]);
      setStatus(s.data);
      setPendingReqs(p.data || []);
    } catch { toast.error("Failed to load publish status"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Refresh whenever any publish/rollback/approval event fires on the page
  useEffect(() => {
    const h = () => { load(); setIframeKey((k) => k + 1); };
    window.addEventListener("vyparcrm:config-published", h);
    return () => window.removeEventListener("vyparcrm:config-published", h);
  }, [load]);

  const requireApproval = !!status?.approval?.require_approval;
  const myEmail = (me?.email || "").toLowerCase();
  const inboxCount = useMemo(
    () => pendingReqs.filter(r => (r.requester_email || "").toLowerCase() !== myEmail).length,
    [pendingReqs, myEmail]
  );

  const publishAll = async () => {
    const msg = requireApproval
      ? "Approval is ON — this will create a publish request that a different Platform Admin must approve. Continue?"
      : "Publish every unpublished change to the live application?";
    if (!window.confirm(msg)) return;
    setPublishing(true);
    try {
      const r = await api.post("/platform/publish-all", {}, { params: { change_note: changeNote || "" } });
      if (r.data?.queued) {
        toast.success("✓ Publish request submitted — awaiting a different Platform Admin");
        setChangeNote("");
        setTab("approvals");
      } else {
        const errCount = r.data.errors?.length || 0;
        if (errCount) toast.error(`Published with ${errCount} error${errCount === 1 ? "" : "s"}`);
        else toast.success(`✓ Published — global version v${r.data.global_version}`);
        window.dispatchEvent(new CustomEvent("vyparcrm:config-published", { detail: { version: r.data.global_version } }));
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    finally { setPublishing(false); }
  };

  const publishOne = async (kind, label) => {
    const msg = requireApproval && kind !== "cms_pages"
      ? `Approval is ON — request publish of ${label}?`
      : `Publish only ${label}?`;
    if (!window.confirm(msg)) return;
    setSingleBusy(kind);
    try {
      const r = await api.post(`/platform/publish/${kind}`, {}, { params: { change_note: changeNote || "" } });
      if (r.data?.queued) {
        toast.success(`✓ Request submitted for ${label}`);
        setTab("approvals");
      } else {
        toast.success(`✓ Published ${label}`);
        window.dispatchEvent(new CustomEvent("vyparcrm:config-published", { detail: { kind } }));
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    finally { setSingleBusy(null); }
  };

  const toggleApproval = async (v) => {
    setTogglingApproval(true);
    try {
      await api.patch("/platform/publish-config", { require_approval: !!v });
      toast.success(v ? "Approval required for future publishes" : "Direct publishing enabled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update setting"); }
    finally { setTogglingApproval(false); }
  };

  const approveReq = async (req) => {
    if (!window.confirm(`Approve request from ${req.requester_email} and publish now?`)) return;
    setReqBusyId(req.id);
    try {
      const r = await api.post(`/platform/publish-approve/${req.id}`);
      toast.success(`✓ Approved — global version v${r.data.global_version}`);
      window.dispatchEvent(new CustomEvent("vyparcrm:config-published", { detail: { version: r.data.global_version } }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
    finally { setReqBusyId(null); }
  };

  const cancelReq = async (req) => {
    if (!window.confirm("Cancel your own request?")) return;
    setReqBusyId(req.id);
    try {
      await api.post(`/platform/publish-cancel/${req.id}`);
      toast.success("Request cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
    finally { setReqBusyId(null); }
  };

  const submitReject = async (reason) => {
    if (!rejectFor || !reason) return;
    setReqBusyId(rejectFor.id);
    try {
      await api.post(`/platform/publish-reject/${rejectFor.id}`, { reason });
      toast.success("Request rejected");
      setRejectFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setReqBusyId(null); }
  };

  if (loading) {
    return <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="publish-loading">Loading publish status…</Card>;
  }
  if (!status) return null;

  const kinds = status.kinds || [];
  const pending = kinds.filter((k) => k.has_unpublished_changes);
  const clean = kinds.filter((k) => !k.has_unpublished_changes);
  const hasAnyPublished = kinds.some((k) => k.version > 0);
  const anythingPending = status.total_pending > 0 || status.cms_pending > 0;

  return (
    <Card className="p-0 overflow-hidden" data-testid="publish-center">
      {splitPreview && (
        <div className="border-b-2 border-amber-400 bg-amber-50 dark:bg-amber-950/20" data-testid="publish-split-preview">
          <div className="p-3 flex items-center justify-between border-b border-amber-300 dark:border-amber-800">
            <div className="text-xs flex items-center gap-2">
              <Eye size={13} weight="fill" className="text-amber-700 dark:text-amber-300" />
              <span className="font-semibold text-amber-900 dark:text-amber-200">Live Draft Preview</span>
              <span className="text-muted-foreground">— what the app will look like the moment you click Publish. Auto-refreshes when drafts change.</span>
            </div>
            <button onClick={() => setIframeKey((k) => k + 1)} className="text-xs underline text-amber-900 dark:text-amber-200 hover:opacity-70"
                    data-testid="publish-preview-refresh">Refresh preview</button>
          </div>
          <iframe
            key={iframeKey}
            title="Draft Preview"
            src="/?preview=draft"
            className="w-full h-[600px] border-0 bg-background"
            data-testid="publish-preview-iframe"
          />
        </div>
      )}
      <div className="p-6 border-b border-border flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="overline text-muted-foreground">Deployment</div>
          <h3 className="font-heading font-bold text-xl">Publish Center</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-lg">
            Every builder edit is a <b>draft</b> until you publish. Publishing snapshots the state as a versioned release; every publish and rollback keeps a permanent history so you can always go back.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setSplitPreview(!splitPreview)}
                  data-testid="publish-split-preview-btn"
                  className={splitPreview ? "bg-amber-100 dark:bg-amber-950 border-amber-500" : ""}>
            <Eye size={13} className="mr-1.5" />{splitPreview ? "Hide preview" : "Live preview"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setPreviewMode(true); window.open("/?preview=draft", "_blank", "noopener"); }}
                  data-testid="publish-open-preview-tab">
            <ArrowSquareOut size={13} className="mr-1.5" />Open in new tab
          </Button>
          <Button size="sm" variant="outline" onClick={() => setDiffOpen(true)} disabled={!anythingPending} data-testid="publish-diff-btn">
            <GitDiff size={13} className="mr-1.5" />Preview diff
          </Button>
          <Button size="sm" variant="outline" onClick={load} data-testid="publish-refresh"><ArrowsClockwise size={13} className="mr-1.5" />Refresh</Button>
          <Button size="sm" onClick={publishAll} disabled={publishing || !anythingPending} data-testid="publish-all">
            {requireApproval ? <PaperPlaneTilt size={14} className="mr-1.5" /> : <CloudArrowUp size={14} className="mr-1.5" />}
            {publishing
              ? (requireApproval ? "Requesting…" : "Publishing…")
              : !anythingPending
                ? "Nothing to publish"
                : requireApproval
                  ? `Request Publish (${status.total_pending})`
                  : `Publish All (${status.total_pending})`}
          </Button>
        </div>
      </div>

      {/* Global summary */}
      <div className="grid sm:grid-cols-5 gap-3 p-6 bg-secondary/20 border-b border-border">
        <div data-testid="pc-global-version">
          <div className="overline text-[10px] text-muted-foreground">Global version</div>
          <div className="font-heading font-bold text-2xl mt-1">v{status.global_version}</div>
        </div>
        <div data-testid="pc-pending-count">
          <div className="overline text-[10px] text-muted-foreground">Unpublished areas</div>
          <div className={"font-heading font-bold text-2xl mt-1 " + (status.total_pending ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300")}>
            {status.total_pending}
          </div>
        </div>
        <div data-testid="pc-cms-pending">
          <div className="overline text-[10px] text-muted-foreground">CMS pending</div>
          <div className={"font-heading font-bold text-2xl mt-1 " + (status.cms_pending ? "text-amber-700 dark:text-amber-300" : "")}>{status.cms_pending}</div>
        </div>
        <div data-testid="pc-requests-count">
          <div className="overline text-[10px] text-muted-foreground">Pending requests</div>
          <div className={"font-heading font-bold text-2xl mt-1 " + ((status.pending_requests || 0) ? "text-sky-700 dark:text-sky-300" : "")}>{status.pending_requests || 0}</div>
        </div>
        <div data-testid="pc-last-publish">
          <div className="overline text-[10px] text-muted-foreground">Last publish</div>
          <div className="text-xs mt-1.5 flex items-center gap-1.5"><Clock size={11} />{status.last_publish ? dt(status.last_publish.published_at) : "Never"}</div>
          {status.last_publish?.published_by_email && (
            <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5"><User size={10} />{status.last_publish.published_by_email}</div>
          )}
        </div>
      </div>

      {/* Approval governance settings */}
      <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap bg-background" data-testid="pc-approval-settings">
        <ShieldCheck size={16} className={requireApproval ? "text-sky-600" : "text-muted-foreground"} weight={requireApproval ? "fill" : "regular"} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Two-step publish approval</div>
          <div className="text-[11px] text-muted-foreground">
            {requireApproval
              ? "Every publish must be reviewed and approved by a different Platform Admin before going live."
              : "Publishes go live immediately. Turn on for teams with multiple Platform Admins."}
          </div>
        </div>
        <Switch checked={requireApproval} disabled={togglingApproval}
                onCheckedChange={toggleApproval} data-testid="approval-toggle" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mx-6 mt-4" data-testid="pc-tabs">
          <TabsTrigger value="publish" data-testid="pc-tab-publish">
            Publish
            {anythingPending && <span className="ml-2 text-[10px] px-1.5 rounded bg-amber-500/20 text-amber-800 dark:text-amber-200">{status.total_pending}</span>}
          </TabsTrigger>
          <TabsTrigger value="approvals" data-testid="pc-tab-approvals">
            <Handshake size={13} className="mr-1.5" />
            Approvals
            {pendingReqs.length > 0 && (
              <span className={"ml-2 text-[10px] px-1.5 rounded " + (inboxCount > 0 ? "bg-sky-500/20 text-sky-800 dark:text-sky-200" : "bg-secondary text-muted-foreground")}>
                {pendingReqs.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="publish" className="mt-0">
          {anythingPending && (
            <div className="px-6 pt-3">
              <label className="overline text-[10px] text-muted-foreground">Change note (optional)</label>
              <Input value={changeNote} onChange={(e) => setChangeNote(e.target.value)}
                     placeholder="What are you shipping? (e.g. Reordered sidebar, added Couriers lookup)"
                     className="mt-1" data-testid="change-note" />
            </div>
          )}

          {pending.length > 0 && (
            <div className="p-6 space-y-3 border-b border-border" data-testid="pc-pending-section">
              <div className="flex items-center gap-2">
                <WarningCircle size={13} weight="fill" className="text-amber-500" />
                <div className="text-xs font-medium uppercase tracking-wider">Unpublished changes</div>
              </div>
              {pending.map((k) => (
                <KindRow key={k.kind} k={k} onPublish={publishOne} onHistory={() => setHistoryFor(k)} busy={singleBusy === k.kind} requireApproval={requireApproval} />
              ))}
              {status.cms_pending > 0 && (
                <div className="p-3 border border-amber-500/40 bg-amber-500/5 rounded-md flex items-center gap-3" data-testid="pc-cms-row">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">CMS pages · {status.cms_pending} pending</div>
                    <div className="text-[11px] text-muted-foreground">Every page with unsaved draft changes will be published on next Publish All.</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => publishOne("cms_pages", "CMS pages")} disabled={singleBusy === "cms_pages"} data-testid="pc-publish-cms">
                    {singleBusy === "cms_pages" ? "Publishing…" : "Publish CMS"}
                  </Button>
                </div>
              )}
            </div>
          )}

          {clean.length > 0 && (
            <div className="p-6 space-y-3" data-testid="pc-clean-section">
              <div className="flex items-center gap-2">
                <CheckCircle size={13} weight="fill" className="text-emerald-500" />
                <div className="text-xs font-medium uppercase tracking-wider">Up to date</div>
              </div>
              {clean.map((k) => (
                <KindRow key={k.kind} k={k} onPublish={publishOne} onHistory={() => setHistoryFor(k)} busy={singleBusy === k.kind} requireApproval={requireApproval} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approvals" className="mt-0 p-6 space-y-3" data-testid="pc-approvals-tab">
          {inboxCount > 0 && (
            <div className="p-3 rounded-md border border-sky-500/40 bg-sky-500/5 text-xs" data-testid="pc-inbox-banner">
              <b>{inboxCount}</b> request{inboxCount === 1 ? "" : "s"} awaiting your approval. Rejecting or approving is only allowed for requests you didn't create yourself.
            </div>
          )}
          {pendingReqs.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground italic" data-testid="pc-no-requests">
              No pending publish requests. {requireApproval ? "Create one from the Publish tab." : "Approval is off — publishes go live immediately."}
            </div>
          )}
          {pendingReqs.map((r) => (
            <RequestRow key={r.id} req={r} currentEmail={me?.email}
                        onApprove={approveReq}
                        onReject={(req) => setRejectFor(req)}
                        onCancel={cancelReq}
                        busyId={reqBusyId} />
          ))}
        </TabsContent>
      </Tabs>

      <HistoryDialog
        open={!!historyFor}
        onOpenChange={(v) => !v && setHistoryFor(null)}
        kind={historyFor?.kind}
        kindLabel={historyFor?.label}
        onChanged={load}
      />
      <DiffDialog open={diffOpen} onOpenChange={setDiffOpen} />
      <RejectDialog
        open={!!rejectFor}
        onOpenChange={(v) => !v && setRejectFor(null)}
        onSubmit={submitReject}
        busy={reqBusyId === rejectFor?.id}
      />
    </Card>
  );
}

function KindRow({ k, onPublish, onHistory, busy, requireApproval }) {
  const pending = k.has_unpublished_changes;
  const btnLabel = busy
    ? (requireApproval ? "Requesting…" : "Publishing…")
    : (requireApproval ? "Request publish" : "Publish");
  return (
    <div className={"p-3 border rounded-md flex items-center gap-3 " +
      (pending ? "border-amber-500/40 bg-amber-500/5" : "border-border")}
      data-testid={`pc-kind-${k.kind}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium flex items-center gap-2">
          {k.label}
          {pending && <span className="text-[9px] uppercase tracking-wider text-amber-700 dark:text-amber-300 border border-amber-500/40 px-1.5 rounded">draft</span>}
          {!pending && k.version > 0 && <span className="text-[9px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 px-1.5 rounded">v{k.version}</span>}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {k.draft_count} row{k.draft_count === 1 ? "" : "s"} in draft{k.version > 0 ? ` · ${k.published_count} live` : " · never published"}
          {k.last_published_at && ` · last: ${dt(k.last_published_at)}`}
        </div>
      </div>
      {k.version > 0 && (
        <button onClick={onHistory} className="p-1.5 rounded border border-border hover:bg-secondary text-muted-foreground hover:text-primary" title="Version history" data-testid={`pc-history-${k.kind}`}>
          <ClockCounterClockwise size={13} weight="duotone" />
        </button>
      )}
      {pending && (
        <Button size="sm" variant="outline" onClick={() => onPublish(k.kind, k.label)} disabled={busy} data-testid={`pc-publish-${k.kind}`}>
          {btnLabel}
        </Button>
      )}
    </div>
  );
}
